import { useEffect } from 'react';

import type { CmsApiSearchLandingPage } from '#/modules/cms/api/types/CmsApiSearchLandingPage';
import { SearchBody } from '#/modules/search/components/Body/SearchBody';
import { SearchResultsHeader } from '#/modules/search/components/Results/Header/SearchResultsHeader';
import { SearchResultsHits } from '#/modules/search/components/Results/Hits/SearchResultsHits';
import {
  useIsSearching,
  useSearchCurrentPageResults,
  useSearchSort,
} from '#/modules/search/contexts/SearchContext';
import { useSaveSearch } from '#/modules/search/hooks/useSaveSearch';
import { useSearchAnalytics } from '#/modules/search/hooks/useSearchAnalytics';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import {
  useSearchSavedListingsStore,
  useSearchSavedListingsSyncAlgoliaHits,
} from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { setItem } from '#/utils/localStorage';

import { SearchDescriptionBody } from './SearchDescriptionBody';

type Props = {
  content: CmsApiSearchLandingPage;
};

export function SearchLandingPage({ content }: Props) {
  const results = useSearchCurrentPageResults();

  const searchType = content.filters.type;

  const isSearching = useIsSearching();
  const { searchLocation } = content.location;
  const sort = useSearchSort();

  const { saveSearch } = useSaveSearch();
  const { setSearchUrlPageIndex, setSearchUrlSort } = useSearchPageActions({
    isInSearchLandingPage: true,
  });

  const { getSearchTrackingData } = useSearchAnalytics();

  const { searchSavedListingsStore } = useSearchSavedListingsStore();
  useSearchSavedListingsSyncAlgoliaHits({
    hits: results?.hits,
    searchSavedListingsStore,
  });

  useEffect(() => {
    setItem('searchLocation', searchLocation);
  }, [searchLocation]);

  return (
    <SearchBody>
      <SearchDescriptionBody content={content} />

      <SearchResultsHeader
        results={results}
        isSearching={isSearching}
        showSortOptions={false}
        sort={sort}
        searchType={searchType}
        saveSearch={saveSearch}
        onChangeSort={setSearchUrlSort}
      />

      <SearchResultsHits
        results={results}
        isSearching={isSearching}
        searchType={searchType}
        searchLocation={searchLocation}
        saveSearch={saveSearch}
        getSearchTrackingData={getSearchTrackingData}
        onChangePageIndex={setSearchUrlPageIndex}
        searchSavedListingsStore={searchSavedListingsStore}
      />
    </SearchBody>
  );
}
